
import {
  duplicateSelection,
  multiplySelection,
  removeTableRowsFromExportedSlide,
  sliceSelection,
} from "./slideOoxml";
import { resolveTableCellFills } from "./tableStyle";
import type {
  AlignEdge,
  AlignInTableConfig,
  ArrangeCorner,
  Axis,
  CircleState,
  DockEdge,
  DuplicateDir,
  MultiplyConfig,
  ReorderAxis,
  SizeMode,
  SliceConfig,
  StretchOp,
  SwapMode,
  TableCellAnchor,
} from "../../../types";
import {
  ASPECT_TOLERANCE,
  DUPLICATE_DELTA,
  FIT_DEFAULT_TITLE_PCT,
  FIT_FOOTER_BAND_PCT,
  FIT_MIN_TOP_MARGIN,
  FIT_SIDE_MARGIN_PCT,
  FIT_TITLE_GAP_PTS,
  MAX_CIRCLE_SHAPES,
  MIN_CIRCLE_SHAPES,
  SLIDE_HEIGHT,
  SLIDE_WIDTH,
  SPACING_STEP,
} from "../../../const";
import { hasPpt, ppt } from "../../../utils/formatUtils";
import { FONT_FIELDS, applyFont, snapshotFont } from "./fontSnapshot";

interface ShapeGeom {
  shape: PowerPoint.Shape;
  left: number;
  top: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
  cx: number;
  cy: number;
}

/**
 * Load the selected shapes with geometry and split them into the reference
 * shape (the last one selected) and the others that will move to align.
 */
async function getSelectedShapesWithRef(
  context: PowerPoint.RequestContext,
  minShapes = 2,
): Promise<{ shapes: ShapeGeom[]; refShape: ShapeGeom; otherShapes: ShapeGeom[] }> {
  const selected = context.presentation.getSelectedShapes();
  selected.load("items");
  await context.sync();

  if (!selected.items || selected.items.length < minShapes) {
    throw new Error(minShapes <= 1 ? "Select at least one shape first." : `Select at least ${minShapes} shapes first.`);
  }

  for (const shape of selected.items) {
    shape.load("left,top,width,height");
  }
  await context.sync();

  const all: ShapeGeom[] = selected.items.map((s) => ({
    shape: s,
    left: s.left,
    top: s.top,
    width: s.width,
    height: s.height,
    right: s.left + s.width,
    bottom: s.top + s.height,
    cx: s.left + s.width / 2,
    cy: s.top + s.height / 2,
  }));

  const refShape = all[all.length - 1]!;
  const otherShapes = all.slice(0, -1);
  return { shapes: all, refShape, otherShapes };
}

/**
 * Align the selected shapes to the reference (last selected) shape's edge or
 * centre line.
 */
export async function alignShapes(edge: AlignEdge): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const { refShape, otherShapes } = await getSelectedShapesWithRef(context);
    for (const s of otherShapes) {
      switch (edge) {
        case "left":
          s.shape.left = refShape.left;
          break;
        case "right":
          s.shape.left = refShape.right - s.width;
          break;
        case "center":
          s.shape.left = refShape.cx - s.width / 2;
          break;
        case "top":
          s.shape.top = refShape.top;
          break;
        case "bottom":
          s.shape.top = refShape.bottom - s.height;
          break;
        case "middle":
          s.shape.top = refShape.cy - s.height / 2;
          break;
      }
    }
    await context.sync();
  });
}

/* ── Arrange group ──────────────────────────────────────────────
 * Ported from `AlignTools` (centralize, _cascade, alignCircle).
 * The interactive Arrange-Circle control panel (radius/angle/size/
 * direction/arc/add/remove) is a separate follow-up; `alignCircle`
 * here is the one-shot circular arrangement from the prototype's
 * initial call.
 */

/**
 * The presentation's actual slide size in points, read from `pageSetup`
 * (PowerPointApi 1.10). Falls back to the widescreen default (960×540) if the
 * host is older or the value is unavailable, so custom/4:3 decks fit correctly.
 */
async function getSlideSize(
  context: PowerPoint.RequestContext,
): Promise<{ width: number; height: number }> {
  try {
    const pageSetup = context.presentation.pageSetup;
    pageSetup.load("slideWidth,slideHeight");
    await context.sync();
    return {
      width: pageSetup.slideWidth || SLIDE_WIDTH,
      height: pageSetup.slideHeight || SLIDE_HEIGHT,
    };
  } catch {
    return { width: SLIDE_WIDTH, height: SLIDE_HEIGHT };
  }
}

/** Centre the bounding box of the selected shapes on the slide. */
export async function centralizeOnSlide(): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const { shapes } = await getSelectedShapesWithRef(context, 1);
    const { width: slideW, height: slideH } = await getSlideSize(context);

    const minL = Math.min(...shapes.map((s) => s.left));
    const minT = Math.min(...shapes.map((s) => s.top));
    const maxR = Math.max(...shapes.map((s) => s.right));
    const maxB = Math.max(...shapes.map((s) => s.bottom));

    const groupW = maxR - minL;
    const groupH = maxB - minT;
    const offsetX = (slideW - groupW) / 2 - minL;
    const offsetY = (slideH - groupH) / 2 - minT;

    for (const s of shapes) {
      s.shape.left = s.left + offsetX;
      s.shape.top = s.top + offsetY;
    }
    await context.sync();
  });
}

/** Cascade the selected shapes from the reference shape's chosen corner. */
export async function arrangeCascade(corner: ArrangeCorner): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const { shapes, refShape } = await getSelectedShapesWithRef(context);
    const step = 20; // points offset per shape

    // Sort by original position to keep relative order.
    const sorted = [...shapes].sort((a, b) => a.left + a.top - (b.left + b.top));

    for (let i = 0; i < sorted.length; i++) {
      const s = sorted[i]!;
      switch (corner) {
        case "topLeft":
          s.shape.left = refShape.left + i * step;
          s.shape.top = refShape.top + i * step;
          break;
        case "topRight":
          s.shape.left = refShape.right - s.width - i * step;
          s.shape.top = refShape.top + i * step;
          break;
        case "bottomLeft":
          s.shape.left = refShape.left + i * step;
          s.shape.top = refShape.bottom - s.height - i * step;
          break;
        case "bottomRight":
          s.shape.left = refShape.right - s.width - i * step;
          s.shape.top = refShape.bottom - s.height - i * step;
          break;
      }
    }
    await context.sync();
  });
}

/**
 * Arrange the selected shapes evenly around a full circle sized from their
 * bounding box, rotating each so text stays readable (one-shot; no panel).
 */
/** Arrangement parameters shared by the initial layout and re-layouts. */
type CircleLayout = Pick<CircleState, "centerX" | "centerY" | "radius" | "angleDeg" | "size" | "direction" | "arc">;

/**
 * Position an ordered list of shapes around a circle, sizing each to `size`%
 * of its base size and rotating it so text stays readable. `angleDeg` rotates
 * the whole arrangement; `direction` and `arc` control the sweep.
 */
/** The angular span (radians) covered by a circle arc mode. */
function arcSpanRadians(arc: "full" | "semi" | "quarter"): number {
  if (arc === "semi") return Math.PI;
  if (arc === "quarter") return Math.PI / 2;
  return 2 * Math.PI;
}

function arrangeInCircle(
  items: ReadonlyArray<{ shape: PowerPoint.Shape; baseWidth: number; baseHeight: number }>,
  layout: CircleLayout,
): void {
  const { centerX, centerY, radius, angleDeg, size, direction, arc } = layout;
  const n = items.length;
  const arcSpan = arcSpanRadians(arc);
  const dirMul = direction === "ccw" ? -1 : 1;
  const angleStep = arcSpan / (arc === "full" ? n : Math.max(n - 1, 1));
  const startAngle = (angleDeg * Math.PI) / 180 - Math.PI / 2; // 0° = top
  const scale = size / 100;

  for (let i = 0; i < n; i++) {
    const it = items[i]!;
    const w = it.baseWidth * scale;
    const h = it.baseHeight * scale;
    it.shape.width = w;
    it.shape.height = h;

    const angle = startAngle + dirMul * angleStep * i;
    it.shape.left = centerX + radius * Math.cos(angle) - w / 2;
    it.shape.top = centerY + radius * Math.sin(angle) - h / 2;

    // Rotate tangent to the circle, flipped when upside-down so text reads.
    let rotDeg = (angle * 180) / Math.PI + 90;
    rotDeg = ((rotDeg % 360) + 360) % 360;
    if (rotDeg > 90 && rotDeg < 270) rotDeg -= 180;
    try {
      (it.shape as unknown as { rotation: number }).rotation = rotDeg;
    } catch {
      /* rotation may be unsupported on this host build */
    }
  }
}

/**
 * Arrange the selected shapes evenly around a circle sized from their bounding
 * box. Returns the resulting {@link CircleState} so the Align Circle panel can
 * re-lay the same shapes out as the user nudges radius / angle.
 */
export async function alignCircle(): Promise<CircleState> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  let result: CircleState | null = null;
  await ppt().run(async (context) => {
    const { shapes } = await getSelectedShapesWithRef(context, MIN_CIRCLE_SHAPES);
    for (const s of shapes) s.shape.load("id");
    await context.sync();

    const minL = Math.min(...shapes.map((s) => s.left));
    const minT = Math.min(...shapes.map((s) => s.top));
    const maxR = Math.max(...shapes.map((s) => s.right));
    const maxB = Math.max(...shapes.map((s) => s.bottom));
    const centerX = (minL + maxR) / 2;
    const centerY = (minT + maxB) / 2;
    let radius = Math.max(maxR - minL, maxB - minT) / 2.5;
    if (radius < 40) radius = 80;

    const baseSizes: Record<string, { width: number; height: number }> = {};
    for (const s of shapes) baseSizes[s.shape.id] = { width: s.width, height: s.height };

    const layout: CircleLayout = {
      centerX,
      centerY,
      radius,
      angleDeg: 0,
      size: 100,
      direction: "cw",
      arc: "full",
    };
    arrangeInCircle(
      shapes.map((s) => ({ shape: s.shape, baseWidth: s.width, baseHeight: s.height })),
      layout,
    );
    await context.sync();
    result = { shapeIds: shapes.map((s) => s.shape.id), baseSizes, ...layout };
  });
  if (!result) throw new Error("Select at least 2 shapes first.");
  return result;
}

/**
 * Every shape on the active slide, keyed by id, with id/width/height loaded —
 * shared by {@link relayoutCircle}, {@link addCircleShape} and
 * {@link removeCircleShape} so each doesn't repeat the same load/sync pair.
 */
async function getActiveSlideShapesById(
  context: PowerPoint.RequestContext,
): Promise<Map<string, PowerPoint.Shape>> {
  const active = await getActiveSlide(context);
  active.shapes.load("items");
  await context.sync();
  for (const s of active.shapes.items) s.load("id,width,height");
  await context.sync();
  return new Map(active.shapes.items.map((s) => [s.id, s] as const));
}

/**
 * Re-lay a previously-arranged circle out with the given state (new radius /
 * angle). Finds the shapes by the ids captured in {@link alignCircle}.
 */
export async function relayoutCircle(state: CircleState): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const byId = await getActiveSlideShapesById(context);
    const items = state.shapeIds
      .map((id) => byId.get(id))
      .filter((s): s is PowerPoint.Shape => s !== undefined)
      .map((s) => {
        const base = state.baseSizes[s.id] ?? { width: s.width, height: s.height };
        return { shape: s, baseWidth: base.width, baseHeight: base.height };
      });
    if (items.length === 0) return;

    arrangeInCircle(items, state);
    await context.sync();
  });
}

/** Reselect the given shapes on the active slide, in one host round trip —
 * used after Add/Remove so the whole (updated) ring stays visually selected,
 * matching how the panel leaves the selection after the initial arrange. */
async function selectShapes(shapeIds: readonly string[]): Promise<void> {
  await ppt().run(async (context) => {
    const active = await getActiveSlide(context);
    active.setSelectedShapes([...shapeIds]);
    await context.sync();
  });
}

/**
 * Duplicate the ring's most-recently-added shape and add the copy to the
 * arrangement, then relayout so every shape — including the new one —
 * settles back onto the ring.
 *
 * PowerPoint's JS API has no generic "duplicate this shape" call, so this
 * temporarily selects the source shape and reuses the native-clone pipeline
 * the Duplicate group's buttons already rely on ({@link duplicateSelection}
 * in `slideOoxml.ts`) instead of re-implementing shape cloning here.
 */
export async function addCircleShape(state: CircleState): Promise<CircleState> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  if (state.shapeIds.length === 0) {
    throw new Error("No shapes to duplicate — re-run Align Circle first.");
  }
  if (state.shapeIds.length >= MAX_CIRCLE_SHAPES) {
    throw new Error(`A circle arrangement can hold at most ${MAX_CIRCLE_SHAPES} shapes.`);
  }
  const sourceId = state.shapeIds[state.shapeIds.length - 1]!;
  const baseSize = state.baseSizes[sourceId];
  if (!baseSize) {
    throw new Error("Couldn't determine the shape's size. Please re-run Align Circle.");
  }

  // Snapshot the slide's shape ids first, then select just the source shape —
  // duplicateSelection() (a separate host round trip below) clones whatever
  // is selected at that point.
  let beforeIds = new Set<string>();
  await ppt().run(async (context) => {
    const byId = await getActiveSlideShapesById(context);
    beforeIds = new Set(byId.keys());
    if (!byId.has(sourceId)) {
      throw new Error(
        "One of the arranged shapes couldn't be found — it may have been deleted. Please re-run Align Circle.",
      );
    }
    const active = await getActiveSlide(context);
    active.setSelectedShapes([sourceId]);
    await context.sync();
  });

  // Offset (0, 0): the clone lands exactly on its source; relayoutCircle
  // below repositions every ring shape (including the new one) right after.
  await duplicateSelection(0, 0);

  let newId: string | undefined;
  await ppt().run(async (context) => {
    const byId = await getActiveSlideShapesById(context);
    newId = [...byId.keys()].find((id) => !beforeIds.has(id));
  });
  if (!newId) throw new Error("Couldn't duplicate the shape. Please try again.");

  const next: CircleState = {
    ...state,
    shapeIds: [...state.shapeIds, newId],
    baseSizes: { ...state.baseSizes, [newId]: baseSize },
  };
  await relayoutCircle(next);
  await selectShapes(next.shapeIds);
  return next;
}

/**
 * Delete the ring's most-recently-added shape and relayout the rest. Refuses
 * to go below {@link MIN_CIRCLE_SHAPES} — the circle's angle math divides by
 * `n` (or `n - 1` for a non-full arc), so fewer shapes leaves nothing
 * meaningful to lay out.
 */
export async function removeCircleShape(state: CircleState): Promise<CircleState> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  if (state.shapeIds.length <= MIN_CIRCLE_SHAPES) {
    throw new Error(`Keep at least ${MIN_CIRCLE_SHAPES} shapes in the circle.`);
  }
  const targetId = state.shapeIds[state.shapeIds.length - 1]!;

  await ppt().run(async (context) => {
    const byId = await getActiveSlideShapesById(context);
    // Already gone (e.g. deleted outside the panel) — nothing to delete;
    // it still gets dropped from the arrangement below.
    byId.get(targetId)?.delete();
    await context.sync();
  });

  const { [targetId]: _removedBaseSize, ...baseSizes } = state.baseSizes;
  const next: CircleState = {
    ...state,
    shapeIds: state.shapeIds.slice(0, -1),
    baseSizes,
  };
  await relayoutCircle(next);
  await selectShapes(next.shapeIds);
  return next;
}

/* ── Distribute group ───────────────────────────────────────────
 * Ported from `AlignTools` (distributeHorizontally/Vertically,
 * _adjustSpacing).
 */

/** Even out the gaps between 3+ shapes across the given axis. */
export async function distributeShapes(axis: Axis): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const { shapes } = await getSelectedShapesWithRef(context, 3);
    if (axis === "horizontal") {
      const sorted = [...shapes].sort((a, b) => a.left - b.left);
      const first = sorted[0]!;
      const last = sorted[sorted.length - 1]!;
      const totalWidth = sorted.reduce((sum, s) => sum + s.width, 0);
      const gap = (last.right - first.left - totalWidth) / (sorted.length - 1);
      let x = first.left;
      for (const s of sorted) {
        s.shape.left = x;
        x += s.width + gap;
      }
    } else {
      const sorted = [...shapes].sort((a, b) => a.top - b.top);
      const first = sorted[0]!;
      const last = sorted[sorted.length - 1]!;
      const totalHeight = sorted.reduce((sum, s) => sum + s.height, 0);
      const gap = (last.bottom - first.top - totalHeight) / (sorted.length - 1);
      let y = first.top;
      for (const s of sorted) {
        s.shape.top = y;
        y += s.height + gap;
      }
    }
    await context.sync();
  });
}

/** Increase/decrease spacing by shifting shapes outward from their centre. */
export async function adjustSpacing(
  axis: Axis,
  direction: "increase" | "decrease",
): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  const delta = (direction === "increase" ? 1 : -1) * SPACING_STEP;
  await ppt().run(async (context) => {
    const { shapes } = await getSelectedShapesWithRef(context);
    const isH = axis === "horizontal";
    const sorted = [...shapes].sort((a, b) => (isH ? a.left - b.left : a.top - b.top));
    for (let i = 0; i < sorted.length; i++) {
      const offset = (i - (sorted.length - 1) / 2) * delta;
      const s = sorted[i]!;
      if (isH) s.shape.left = s.left + offset;
      else s.shape.top = s.top + offset;
    }
    await context.sync();
  });
}

/* ── Duplicate group ────────────────────────────────────────────
 * `stackHorizontal/stackVertical` are pure geometry (ported).
 * The four directional duplicates clone shapes at the OOXML level
 * (see slideOoxml.ts) to preserve fills, gradients, effects, images,
 * groups and text runs — the Office.js API has no shape-clone.
 */

/** Snap shapes edge-to-edge along the given axis (no gaps). */
export async function stackShapes(axis: Axis): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const { shapes } = await getSelectedShapesWithRef(context, 2);
    const isH = axis === "horizontal";
    const sorted = [...shapes].sort((a, b) => (isH ? a.left - b.left : a.top - b.top));
    let prevStart = isH ? sorted[0]!.left : sorted[0]!.top;
    let prevSize = isH ? sorted[0]!.width : sorted[0]!.height;
    for (let i = 1; i < sorted.length; i++) {
      const newStart = prevStart + prevSize;
      const s = sorted[i]!;
      if (isH) s.shape.left = newStart;
      else s.shape.top = newStart;
      prevStart = newStart;
      prevSize = isH ? s.width : s.height;
    }
    await context.sync();
  });
}

/** Full-fidelity directional duplicate via the OOXML slide pipeline. */
export async function duplicateShapes(dir: DuplicateDir): Promise<void> {
  const [dx, dy] = DUPLICATE_DELTA[dir];
  await duplicateSelection(dx, dy);
}

/** Multiply the selected shape into a rows × cols grid (OOXML clone). */
export async function multiplyObject(cfg: MultiplyConfig): Promise<void> {
  await multiplySelection(cfg);
}

/** Slice the selected shape into rows × cols equal pieces (OOXML clone). */
export async function sliceObject(cfg: SliceConfig): Promise<void> {
  await sliceSelection(cfg);
}

/* ── Dock group ─────────────────────────────────────────────────
 * Ported from `AlignTools` (dockLeft/Right/Top/Bottom/TopLeft/
 * BottomRight). Docks the other shapes just outside the reference
 * (last selected) shape's edge/corner.
 */

export async function dockShapes(edge: DockEdge): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const { refShape, otherShapes } = await getSelectedShapesWithRef(context);
    for (const s of otherShapes) {
      switch (edge) {
        case "left":
          s.shape.left = refShape.left - s.width;
          break;
        case "right":
          s.shape.left = refShape.right;
          break;
        case "top":
          s.shape.top = refShape.top - s.height;
          break;
        case "bottom":
          s.shape.top = refShape.bottom;
          break;
        case "topLeft":
          s.shape.left = refShape.left - s.width;
          s.shape.top = refShape.top - s.height;
          break;
        case "bottomRight":
          s.shape.left = refShape.right;
          s.shape.top = refShape.bottom;
          break;
      }
    }
    await context.sync();
  });
}

/* ── Swap group ─────────────────────────────────────────────────
 * Ported from `AlignTools` (swapHorizontally/Vertically/Objects/Text/
 * Color). Every swap operates on exactly two selected shapes and
 * exchanges one attribute between them:
 *   - horizontal → left,  vertical → top,  objects → left + top
 *   - text       → textFrame text + its run formatting
 *   - color      → solid fill colour
 */

/**
 * Run-level font properties Office.js can read and write, so text swaps keep
 * their formatting (bold, colour, size, …). Highlight isn't exposed by the API,
 * and mixed multi-run formatting reads as `null` — both can't be preserved.
 */
/** Exchange a position/text/colour attribute between exactly two shapes. */
export async function swapShapes(mode: SwapMode): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const { shapes } = await getSelectedShapesWithRef(context, 2);
    if (shapes.length !== 2) throw new Error("Select exactly 2 shapes to swap.");
    const a = shapes[0];
    const b = shapes[1];
    if (!a || !b) throw new Error("Select exactly 2 shapes to swap.");

    // a.left/a.top are the captured originals, so writing to the proxies in
    // either order still swaps correctly.
    switch (mode) {
      case "horizontal":
        a.shape.left = b.left;
        b.shape.left = a.left;
        break;
      case "vertical":
        a.shape.top = b.top;
        b.shape.top = a.top;
        break;
      case "objects":
        a.shape.left = b.left;
        a.shape.top = b.top;
        b.shape.left = a.left;
        b.shape.top = a.top;
        break;
      case "text": {
        const rangeA = a.shape.textFrame.textRange;
        const rangeB = b.shape.textFrame.textRange;
        rangeA.load("text");
        rangeB.load("text");
        rangeA.font.load(FONT_FIELDS.join(","));
        rangeB.font.load(FONT_FIELDS.join(","));
        await context.sync();
        // Capture text + formatting before mutating either range.
        const textA = rangeA.text;
        const textB = rangeB.text;
        const fontA = snapshotFont(rangeA.font);
        const fontB = snapshotFont(rangeB.font);
        // Swap the text, then re-apply the other shape's formatting over it.
        rangeA.text = textB;
        rangeB.text = textA;
        applyFont(rangeA.font, fontB);
        applyFont(rangeB.font, fontA);
        break;
      }
      case "color": {
        a.shape.fill.load("type,foregroundColor");
        b.shape.fill.load("type,foregroundColor");
        await context.sync();
        const aType = String(a.shape.fill.type ?? "").toLowerCase();
        const bType = String(b.shape.fill.type ?? "").toLowerCase();
        // Only solid fills carry a foreground colour to exchange.
        const colorA = aType === "solid" ? a.shape.fill.foregroundColor : null;
        const colorB = bType === "solid" ? b.shape.fill.foregroundColor : null;
        if (colorB) a.shape.fill.setSolidColor(colorB);
        else if (bType === "nofill") a.shape.fill.clear();
        if (colorA) b.shape.fill.setSolidColor(colorA);
        else if (aType === "nofill") b.shape.fill.clear();
        break;
      }
    }
    await context.sync();
  });
}

/* ── Layout group ───────────────────────────────────────────────
 * Ported from the Deckorate prototype `ShapeLayoutTools`
 * (utils/shapeLayoutTools.js). Covers the first two Layout operations —
 * both pure Office.js geometry (no OOXML):
 *   - fitToSlide(preserveAspect) — scale + centre the selection (or, when
 *     nothing is selected, the slide's body shapes) into a safe area
 *     computed from the current slide's title / footer / margins.
 *   - smartAlign() — snap selected shapes to shared row/column lines with
 *     minimal movement (tightens a grid without reflowing it).
 * Multiply + Slice (OOXML cloning + a config dialog) are a follow-up.
 */

type TitlePlacement = "top" | "bottom" | "left" | "right" | "none";

/** Minimal shape geometry used by the Layout heuristics. */
interface ShapeBox {
  id: string;
  name: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A slide shape paired with its live proxy so geometry can be written back. */
interface FitShape extends ShapeBox {
  shape: PowerPoint.Shape;
}

/** Score how likely a shape is the slide's title, purely from geometry. */
function titleHeuristicScore(s: ShapeBox, slideW: number, slideH: number): number {
  const topFrac = s.top / slideH;
  const bottomFrac = (s.top + s.height) / slideH;
  const leftFrac = s.left / slideW;
  const rightFrac = (s.left + s.width) / slideW;
  const widthFrac = s.width / slideW;
  const heightFrac = s.height / slideH;

  if (bottomFrac < 0.3 && widthFrac > 0.4) return 2.0 + widthFrac; // top banner
  if (leftFrac < 0.05 && rightFrac < 0.4 && heightFrac > 0.35) return 1.5 + heightFrac; // left
  if (rightFrac > 0.95 && leftFrac > 0.6 && heightFrac > 0.35) return 1.5 + heightFrac; // right
  if (topFrac > 0.7 && widthFrac > 0.4) return 1.2 + widthFrac; // bottom banner
  return 0;
}

/** Find the title on a slide: name starting with "Title", else best geometry. */
function findTitleShape(shapes: ShapeBox[], slideW: number, slideH: number): ShapeBox | null {
  if (shapes.length === 0) return null; // empty slide — no title to find (expected, not an error)
  for (const s of shapes) {
    const n = (s.name || "").toLowerCase();
    if (n.startsWith("title") && !n.includes("subtitle")) return s;
  }
  let best: ShapeBox | null = null;
  let bestScore = 0;
  for (const s of shapes) {
    if (!s.width || !s.height) continue;
    const score = titleHeuristicScore(s, slideW, slideH);
    if (score > bestScore) {
      bestScore = score;
      best = s;
    }
  }
  return bestScore >= 1.0 ? best : null;
}

/** Classify where the title sits: top / bottom / left / right / none. */
function classifyTitlePlacement(
  title: ShapeBox | null,
  slideW: number,
  slideH: number,
): TitlePlacement {
  if (!title) return "none";
  const centerX = title.left + title.width / 2;
  const centerY = title.top + title.height / 2;
  const bottomFrac = (title.top + title.height) / slideH;
  const topFrac = title.top / slideH;
  const leftFrac = title.left / slideW;
  const rightFrac = (title.left + title.width) / slideW;
  const aspect = title.width / Math.max(title.height, 1);

  if (aspect > 2) {
    if (bottomFrac < 0.35) return "top";
    if (topFrac > 0.65) return "bottom";
  }
  if (centerX < slideW * 0.35 && leftFrac < 0.1 && rightFrac < 0.45) return "left";
  if (centerX > slideW * 0.65 && rightFrac > 0.9 && leftFrac > 0.55) return "right";
  if (centerY < slideH * 0.35) return "top";
  return "top"; // safest default
}

interface SafeArea {
  safeLeft: number;
  safeTop: number;
  safeW: number;
  safeH: number;
}

/** Derive the safe body area by carving out title footprint, footer, margins. */
function computeSafeArea(
  title: ShapeBox | null,
  placement: TitlePlacement,
  slideW: number,
  slideH: number,
): SafeArea {
  const margin = slideW * FIT_SIDE_MARGIN_PCT;
  const footer = slideH * FIT_FOOTER_BAND_PCT;
  const gap = FIT_TITLE_GAP_PTS;

  let safeLeft = margin;
  let safeTop = FIT_MIN_TOP_MARGIN;
  let safeW = slideW - 2 * margin;
  let safeH = slideH - FIT_MIN_TOP_MARGIN - footer;

  if (!title) {
    safeTop = slideH * FIT_DEFAULT_TITLE_PCT;
    safeH = slideH - safeTop - footer;
    return { safeLeft, safeTop, safeW, safeH };
  }

  switch (placement) {
    case "top":
      safeTop = title.top + title.height + gap;
      safeH = slideH - safeTop - footer;
      break;
    case "bottom":
      safeTop = FIT_MIN_TOP_MARGIN;
      safeH = title.top - safeTop - gap;
      break;
    case "left":
      safeLeft = title.left + title.width + gap;
      safeW = slideW - safeLeft - margin;
      safeTop = FIT_MIN_TOP_MARGIN;
      safeH = slideH - safeTop - footer;
      break;
    case "right":
      safeLeft = margin;
      safeW = title.left - margin - gap;
      safeTop = FIT_MIN_TOP_MARGIN;
      safeH = slideH - safeTop - footer;
      break;
    default:
      break;
  }

  // Clamp to a viable minimum so degenerate titles can't produce a tiny area.
  if (safeW < 50) {
    safeW = slideW - 2 * margin;
    safeLeft = margin;
  }
  if (safeH < 50) safeH = slideH - safeTop - footer;
  return { safeLeft, safeTop, safeW, safeH };
}

/** True if a shape overlaps the slide at all (connectors tested by midpoint). */
function isOnSlide(s: ShapeBox, slideW: number, slideH: number): boolean {
  const w = Math.max(0, s.width || 0);
  const h = Math.max(0, s.height || 0);
  if (w === 0 || h === 0) {
    const x = s.left + w / 2;
    const y = s.top + h / 2;
    return x >= -1 && x <= slideW + 1 && y >= -1 && y <= slideH + 1;
  }
  const xOverlap = Math.max(0, Math.min(s.left + w, slideW) - Math.max(s.left, 0));
  const yOverlap = Math.max(0, Math.min(s.top + h, slideH) - Math.max(s.top, 0));
  return xOverlap * yOverlap > 0;
}

/** True for a tiny corner-pinned ghost marker (e.g. a 13×13 rect at 0,0). */
function isGhostMarker(s: ShapeBox, slideW: number, slideH: number): boolean {
  const w = s.width || 0;
  const h = s.height || 0;
  if (w <= 0.5 || h <= 0.5) return false; // skip lines/points
  if (w >= slideW * 0.03 || h >= slideH * 0.03) return false;
  const tol = 0.02;
  const nearLeft = s.left < slideW * tol;
  const nearRight = s.left + w > slideW * (1 - tol);
  const nearTop = s.top < slideH * tol;
  const nearBottom = s.top + h > slideH * (1 - tol);
  return (nearLeft || nearRight) && (nearTop || nearBottom);
}

/** True for a full-slide backdrop rectangle anchored near the origin. */
function isSlideBackdrop(s: ShapeBox, slideW: number, slideH: number): boolean {
  const w = s.width || 0;
  const h = s.height || 0;
  if (w <= 0 || h <= 0) return false;
  const widthCoverage = w / slideW;
  const heightCoverage = h / slideH;
  const areaCoverage = (w * h) / Math.max(1, slideW * slideH);
  if (Math.abs(s.left) >= slideW * 0.03 || Math.abs(s.top) >= slideH * 0.03) return false;
  if (widthCoverage > 0.95 && heightCoverage > 0.95) return true; // strict
  if (areaCoverage > 0.8 && (widthCoverage > 0.9 || heightCoverage > 0.9)) return true; // lenient
  return false;
}

/**
 * Subtitle shapes that should stay anchored to the title (excluded from the
 * body fit). Name-first + STRICT proximity: a shape must be named "Subtitle…"
 * AND sit immediately adjacent to the title with matching column/width — this
 * avoids misclassifying ordinary body text sitting under the title.
 */
/** Dimensional gate: a subtitle is a title-width band, neither tiny nor oversized. */
function subtitleSizeOk(s: ShapeBox, title: ShapeBox): boolean {
  if (!s.width || !s.height || s.width <= 0 || s.height <= 0) return false;
  if (s.height < 10) return false;
  if (s.height > title.height * 1.1) return false;
  if (s.width < title.width * 0.65) return false;
  if (s.width > title.width * 1.1) return false;
  const sArea = s.width * s.height;
  const titleArea = Math.max(1, title.width * title.height);
  if (sArea < titleArea * 0.25) return false;
  if (sArea > titleArea * 1.2) return false;
  return true;
}

/** Positional gate: sits in the title's column and immediately above/below it. */
function subtitleAdjacentToTitle(
  s: ShapeBox,
  title: ShapeBox,
  slideW: number,
  slideH: number,
): boolean {
  const MAX_GAP_BELOW_PT = 10;
  const MAX_GAP_ABOVE_PT = 8;
  if (s.top > slideH * 0.45) return false; // top region only
  if (Math.abs(s.left - title.left) > slideW * 0.04) return false;
  const hOverlap =
    Math.min(s.left + s.width, title.left + title.width) - Math.max(s.left, title.left);
  if (hOverlap <= 0) return false;
  if (hOverlap / s.width < 0.8) return false;
  const gapBelow = s.top - (title.top + title.height);
  const gapAbove = title.top - (s.top + s.height);
  if (gapBelow >= -3 && gapBelow <= MAX_GAP_BELOW_PT) return true;
  return gapAbove >= -3 && gapAbove <= MAX_GAP_ABOVE_PT;
}

function findSubtitleShapes(
  shapes: ShapeBox[],
  title: ShapeBox | null,
  slideW: number,
  slideH: number,
): ShapeBox[] {
  if (shapes.length === 0 || !title) return [];
  const result: ShapeBox[] = [];
  for (const s of shapes) {
    if (s.id === title.id) continue;
    if (!/\bsub[\s_-]*title\b/.test((s.name || "").toLowerCase())) continue;
    if (!subtitleSizeOk(s, title)) continue;
    if (!subtitleAdjacentToTitle(s, title, slideW, slideH)) continue;
    result.push(s);
  }
  return result;
}

/** Drop footer / page-number / date placeholders and small bottom-band shapes. */
function filterFooterShapes<T extends ShapeBox>(shapes: T[], slideH: number): T[] {
  if (shapes.length === 0) return [];
  const filtered = shapes.filter((s) => {
    const name = (s.name || "").toLowerCase();
    if (/\b(footer|slide\s*number|page\s*number|date\s*placeholder|datetime)\b/.test(name)) {
      return false;
    }
    if (s.width <= 0 || s.height <= 0) return true; // keep connectors/dividers
    const bottomFrac = (s.top + s.height) / slideH;
    const heightFrac = s.height / slideH;
    if (bottomFrac > 0.88 && heightFrac < 0.1) return false;
    return true;
  });
  return filtered.length > 0 ? filtered : shapes;
}

/** Axis-aligned bounding box of a shape list. */
function bboxOf(shapes: ShapeBox[]): { left: number; top: number; width: number; height: number } {
  let l = Infinity;
  let t = Infinity;
  let r = -Infinity;
  let b = -Infinity;
  for (const s of shapes) {
    l = Math.min(l, s.left);
    t = Math.min(t, s.top);
    r = Math.max(r, s.left + s.width);
    b = Math.max(b, s.top + s.height);
  }
  return { left: l, top: t, width: r - l, height: b - t };
}

/**
 * Iteratively drop small outlier shapes (< 6% of bbox area) whose removal
 * shrinks the bbox by > 20% — e.g. a stray "Source:" citation in a corner.
 * Never drops below 2 shapes.
 */
/**
 * Index of the single best outlier to drop from `current` — a shape under 6% of
 * the bbox area whose removal shrinks the bbox by more than 20% (largest gain
 * wins). Returns -1 when nothing qualifies or the bbox is degenerate.
 */
function findBestOutlier<T extends ShapeBox>(current: T[]): number {
  const bbox = bboxOf(current);
  const bboxArea = bbox.width * bbox.height;
  if (bboxArea <= 0) return -1;

  let bestIdx = -1;
  let bestGain = 0;
  for (let i = 0; i < current.length; i++) {
    const rest = current.slice(0, i).concat(current.slice(i + 1));
    const restBbox = bboxOf(rest);
    const restArea = restBbox.width * restBbox.height;
    if (restArea <= 0) continue;
    const gain = 1 - restArea / bboxArea;
    const item = current[i]!;
    const ownFrac = (item.width * item.height) / bboxArea;
    if (ownFrac < 0.06 && gain > 0.2 && gain > bestGain) {
      bestGain = gain;
      bestIdx = i;
    }
  }
  return bestIdx;
}

function trimOutliers<T extends ShapeBox>(candidates: T[]): T[] {
  if (candidates.length <= 2) return candidates;
  let current = candidates.slice();
  const maxIter = Math.max(0, current.length - 2);
  for (let iter = 0; iter < maxIter; iter++) {
    if (current.length <= 2) break;
    const bestIdx = findBestOutlier(current);
    if (bestIdx < 0) break;
    current = current.slice(0, bestIdx).concat(current.slice(bestIdx + 1));
  }
  return current;
}

/**
 * Group shapes into rows ("y") or columns ("x") by VISUAL OVERLAP along the
 * axis: two shapes join a group if their extents overlap by more than
 * `minOverlap` of the smaller shape's extent. Union-Find merges transitively.
 */
/** Minimal union-find over the index range [0, n) with path halving. */
function makeUnionFind(n: number): {
  find: (i: number) => number;
  union: (a: number, b: number) => void;
} {
  const parent = Array.from({ length: n }, (_, i) => i);
  const find = (i: number): number => {
    let root = i;
    while (parent[root] !== root) {
      parent[root] = parent[parent[root]!]!;
      root = parent[root]!;
    }
    return root;
  };
  const union = (a: number, b: number): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  };
  return { find, union };
}

/** True when two shapes overlap along the axis by more than `minOverlap`. */
function axisOverlapExceeds(
  a: ShapeBox,
  b: ShapeBox,
  startKey: "top" | "left",
  sizeKey: "height" | "width",
  minOverlap: number,
): boolean {
  const overlap =
    Math.min(a[startKey] + a[sizeKey], b[startKey] + b[sizeKey]) -
    Math.max(a[startKey], b[startKey]);
  if (overlap <= 0) return false;
  const minExtent = Math.min(a[sizeKey], b[sizeKey]);
  return minExtent > 0 && overlap / minExtent > minOverlap;
}

function groupByAxisOverlap<T extends ShapeBox>(
  shapes: T[],
  axis: "x" | "y",
  minOverlap: number,
): T[][] {
  const n = shapes.length;
  const { find, union } = makeUnionFind(n);
  const startKey = axis === "y" ? "top" : "left";
  const sizeKey = axis === "y" ? "height" : "width";

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (axisOverlapExceeds(shapes[i]!, shapes[j]!, startKey, sizeKey, minOverlap)) union(i, j);
    }
  }

  const groupMap = new Map<number, T[]>();
  for (let i = 0; i < n; i++) {
    const r = find(i);
    const g = groupMap.get(r);
    if (g) g.push(shapes[i]!);
    else groupMap.set(r, [shapes[i]!]);
  }
  return [...groupMap.values()];
}

/** Snapshot a live shape proxy into a FitShape (props must already be loaded). */
function toFitShape(s: PowerPoint.Shape): FitShape {
  return { shape: s, id: s.id, name: s.name, left: s.left, top: s.top, width: s.width, height: s.height };
}

/**
 * Auto-fit body set: strip the noise (off-slide, ghost markers, backdrops, the
 * title, subtitles, footers) and trim outliers so the bbox reflects real content.
 */
function selectBodyShapes(
  slideShapes: FitShape[],
  title: ShapeBox | null,
  slideW: number,
  slideH: number,
): FitShape[] {
  const titleId = title?.id;
  const rawValid = slideShapes.filter((s) => s.width > 1 || s.height > 1);
  const onSlide = rawValid.filter((s) => isOnSlide(s, slideW, slideH));
  const noNoise = onSlide.filter(
    (s) => !isGhostMarker(s, slideW, slideH) && !isSlideBackdrop(s, slideW, slideH),
  );
  const nonTitle = noNoise.filter((s) => !titleId || s.id !== titleId);
  const subtitleIds = new Set(findSubtitleShapes(noNoise, title, slideW, slideH).map((s) => s.id));
  const noSubtitles = nonTitle.filter((s) => !subtitleIds.has(s.id));
  return trimOutliers(filterFooterShapes(noSubtitles, slideH));
}

/**
 * Decide which shapes to fit: the user's selection (title excluded) when there
 * is one, otherwise the auto-detected body. Throws a user-facing error when the
 * resulting set is empty.
 */
function resolveShapesToFit(
  hasSelection: boolean,
  selectedItems: PowerPoint.Shape[],
  slideShapes: FitShape[],
  title: ShapeBox | null,
  slideW: number,
  slideH: number,
): FitShape[] {
  const titleId = title?.id;
  if (hasSelection) {
    const shapesToFit = selectedItems.map(toFitShape).filter((s) => !titleId || s.id !== titleId);
    if (shapesToFit.length === 0) {
      throw new Error("Select the body content to fit, not just the title.");
    }
    return shapesToFit;
  }
  const body = selectBodyShapes(slideShapes, title, slideW, slideH);
  if (body.length === 0) throw new Error("There's no body content to fit on this slide.");
  return body;
}

/** Bounding box of the fit set (NOT clamped — fit can pull off-slide content in). */
function fitBounds(shapes: FitShape[]): {
  minL: number;
  minT: number;
  bboxW: number;
  bboxH: number;
} {
  let minL = Infinity;
  let minT = Infinity;
  let maxR = -Infinity;
  let maxB = -Infinity;
  for (const s of shapes) {
    minL = Math.min(minL, s.left);
    minT = Math.min(minT, s.top);
    maxR = Math.max(maxR, s.left + s.width);
    maxB = Math.max(maxB, s.top + s.height);
  }
  return { minL, minT, bboxW: Math.max(maxR - minL, 1), bboxH: Math.max(maxB - minT, 1) };
}

/** Scale factors to map the bbox into the safe area (uniform when preserving aspect). */
function computeFitScale(
  bboxW: number,
  bboxH: number,
  safeW: number,
  safeH: number,
  preserveAspect: boolean,
): { scaleX: number; scaleY: number } {
  if (preserveAspect) {
    const uniform = Math.min(safeW / bboxW, safeH / bboxH);
    return { scaleX: uniform, scaleY: uniform };
  }
  return { scaleX: safeW / bboxW, scaleY: safeH / bboxH };
}

/** Scale + reposition each shape into the centred, scaled bbox inside the safe area. */
function applyFit(
  shapesToFit: FitShape[],
  bounds: { minL: number; minT: number; bboxW: number; bboxH: number },
  scaleX: number,
  scaleY: number,
  preserveAspect: boolean,
  safe: SafeArea,
): void {
  const { minL, minT, bboxW, bboxH } = bounds;
  const newBboxW = bboxW * scaleX;
  const newBboxH = bboxH * scaleY;
  const newBboxL = safe.safeLeft + (safe.safeW - newBboxW) / 2;
  const newBboxT = safe.safeTop + (safe.safeH - newBboxH) / 2;

  for (const s of shapesToFit) {
    const relX = (s.left - minL) / bboxW;
    const relY = (s.top - minT) / bboxH;
    // Keep squares/circles square even in stretch mode.
    let localSX = scaleX;
    let localSY = scaleY;
    if (!preserveAspect && s.height > 0 && Math.abs(s.width / s.height - 1) < ASPECT_TOLERANCE) {
      const minSc = Math.min(scaleX, scaleY);
      localSX = minSc;
      localSY = minSc;
    }
    s.shape.width = s.width * localSX;
    s.shape.height = s.height * localSY;
    s.shape.left = newBboxL + relX * newBboxW;
    s.shape.top = newBboxT + relY * newBboxH;
  }
}

/**
 * Scale + reposition the current selection so it fits a safe area computed
 * from the active slide. With no selection, auto-fits the slide's body shapes
 * (title, subtitles, footers, ghost/backdrop shapes and outliers excluded).
 *
 * @param preserveAspect true → uniform scale; false → stretch to fill.
 */
export async function fitToSlide(preserveAspect = true): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const selected = context.presentation.getSelectedShapes();
    selected.load("items");
    await context.sync();
    const hasSelection = !!selected.items && selected.items.length > 0;
    for (const s of selected.items) s.load("id,name,left,top,width,height");

    // Every shape on the active slide — needed to locate the title + body.
    const activeSlides = context.presentation.getSelectedSlides();
    activeSlides.load("items");
    await context.sync();
    const slide = activeSlides.items[0];
    if (!slide) throw new Error("Couldn't detect the active slide. Please try again.");
    slide.shapes.load("items");
    await context.sync();
    for (const s of slide.shapes.items) s.load("id,name,left,top,width,height");
    await context.sync();

    const { width: slideW, height: slideH } = await getSlideSize(context);
    const slideShapes = slide.shapes.items.map(toFitShape);

    const title = findTitleShape(slideShapes, slideW, slideH);
    const placement = classifyTitlePlacement(title, slideW, slideH);
    const safe = computeSafeArea(title, placement, slideW, slideH);

    // Choose the shapes to fit. With a selection we respect the user's choice
    // (title excluded if it happened to be selected); without one we auto-fit
    // the body, stripping the noise that would otherwise distort the bbox.
    const shapesToFit = resolveShapesToFit(
      hasSelection,
      selected.items,
      slideShapes,
      title,
      slideW,
      slideH,
    );

    const bounds = fitBounds(shapesToFit);
    if (!Number.isFinite(bounds.bboxW) || !Number.isFinite(bounds.bboxH)) {
      throw new TypeError("Invalid selection bounds.");
    }

    const { scaleX, scaleY } = computeFitScale(
      bounds.bboxW,
      bounds.bboxH,
      safe.safeW,
      safe.safeH,
      preserveAspect,
    );

    applyFit(shapesToFit, bounds, scaleX, scaleY, preserveAspect, safe);
    await context.sync();
  });
}

/**
 * Snap selected shapes to shared row/column lines with minimal movement.
 * Shapes whose vertical extents overlap by > 50% form a row (their tops snap
 * to the row's median top); likewise columns snap lefts. Shapes already within
 * 0.5pt of the median are left alone, so a 6×3 grid stays 6×3 — this tightens
 * alignment without reflowing the layout.
 */
/**
 * For each multi-shape group, snap members off the group's median start (`top`
 * for rows, `left` for columns) onto it — but only those more than 0.5pt away,
 * so an already-aligned grid is left untouched. Returns id → new coordinate.
 */
function computeAxisSnaps(groups: ShapeBox[][], key: "top" | "left"): Map<string, number> {
  const snaps = new Map<string, number>();
  for (const group of groups) {
    if (group.length < 2) continue;
    const vals = group.map((s) => s[key]).sort((a, b) => a - b);
    const median = vals[Math.floor(vals.length / 2)]!;
    for (const s of group) {
      if (Math.abs(s[key] - median) > 0.5) snaps.set(s.id, median);
    }
  }
  return snaps;
}

export async function smartAlign(): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const selected = context.presentation.getSelectedShapes();
    selected.load("items");
    await context.sync();
    if (!selected.items || selected.items.length < 2) {
      throw new Error("Select at least 2 shapes to arrange.");
    }
    for (const s of selected.items) s.load("id,name,left,top,width,height");
    await context.sync();

    const boxes = selected.items.map(toFitShape);
    const newTops = computeAxisSnaps(groupByAxisOverlap(boxes, "y", 0.5), "top");
    const newLefts = computeAxisSnaps(groupByAxisOverlap(boxes, "x", 0.5), "left");

    if (newTops.size === 0 && newLefts.size === 0) return; // already aligned

    for (const s of boxes) {
      const nt = newTops.get(s.id);
      const nl = newLefts.get(s.id);
      if (nt !== undefined) s.shape.top = nt;
      if (nl !== undefined) s.shape.left = nl;
    }
    await context.sync();
  });
}

/* ── Size group ─────────────────────────────────────────────────
 * Ported from `AlignTools` (sameWidth/sameHeight/sameWidthLeft/
 * sameHeightTop/sameSize). Each op resizes every non-reference
 * selected shape to match the reference (last selected) shape:
 *   - width      → match width
 *   - height     → match height
 *   - widthLeft  → match width, anchoring the left edge
 *   - heightTop  → match height, anchoring the top edge
 *   - size       → match both width and height
 * The prototype never repositions a shape on resize, so PowerPoint's
 * default top-left anchoring already keeps the left/top edge fixed —
 * `widthLeft`/`heightTop` are therefore behaviourally identical to
 * `width`/`height` and are kept distinct only for intent and iconography.
 */

/** Resize non-reference shapes to match the reference (last selected) shape. */
export async function resizeShapes(mode: SizeMode): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const { refShape, otherShapes } = await getSelectedShapesWithRef(context);
    for (const s of otherShapes) {
      switch (mode) {
        case "width":
        case "widthLeft":
          s.shape.width = refShape.width;
          break;
        case "height":
        case "heightTop":
          s.shape.height = refShape.height;
          break;
        case "size":
          s.shape.width = refShape.width;
          s.shape.height = refShape.height;
          break;
      }
    }
    await context.sync();
  });
}

/* ── Stretch & Fill group ────────────────────────────────────────
 * Ported from `AlignTools` (stretchLeft/Right/Up/Down, pullLeft/Right,
 * edgeMatchTop/Bottom). Each op resizes every non-reference selected
 * shape so one of its edges meets the reference (last selected) shape:
 *   - stretch*      → move the near edge to the reference edge, growing
 *                     the shape to keep its far edge fixed
 *   - pull*         → shrink from an edge, but only when the reference
 *                     edge lies inside the shape (never grows it)
 *   - edgeMatch*    → snap the top/bottom edge to the reference's, resizing
 *                     to keep the opposite edge fixed
 * The captured `left/right/top/bottom` are the originals; writes go to the
 * live `shape` proxy, so read/write order within an op is irrelevant.
 * `edgeMatchTop`/`edgeMatchBottom` are geometrically identical to
 * `stretchUp`/`stretchDown` in the prototype and kept distinct only for
 * intent and iconography.
 */

/** Resize non-reference shapes so an edge meets the reference (last selected) shape. */
export async function stretchShapes(op: StretchOp): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const { refShape, otherShapes } = await getSelectedShapesWithRef(context);
    for (const s of otherShapes) {
      switch (op) {
        case "stretchLeft":
          s.shape.left = refShape.left;
          s.shape.width = s.right - refShape.left;
          break;
        case "stretchRight":
          s.shape.width = refShape.right - s.left;
          break;
        case "stretchUp":
        case "edgeMatchTop":
          s.shape.top = refShape.top;
          s.shape.height = s.bottom - refShape.top;
          break;
        case "stretchDown":
        case "edgeMatchBottom":
          s.shape.height = refShape.bottom - s.top;
          break;
        case "pullLeft":
          // Shrink from the left: move left edge to ref's left, reduce width.
          if (refShape.left > s.left) {
            s.shape.width = s.right - refShape.left;
            s.shape.left = refShape.left;
          }
          break;
        case "pullRight":
          // Shrink from the right: set width so right edge matches ref's right.
          if (refShape.right < s.right) {
            s.shape.width = refShape.right - s.left;
          }
          break;
      }
    }
    await context.sync();
  });
}

/* ── Text Format group ──────────────────────────────────────────
 * Ported from the POC `AlignTools` text operations. Office.js has no
 * paragraph-level API, so the paragraph-based POC ops (split / replace-font)
 * operate on whole-shape text/font instead; tables use the typed
 * `shape.getTable()` API.
 */

interface FontSnap {
  name?: string;
  size?: number;
  bold?: boolean;
  italic?: boolean;
  color?: string;
}

/** Snapshot a text range's (uniform) font. */
function readFontSnap(font: PowerPoint.ShapeFont): FontSnap {
  return {
    name: font.name ?? undefined,
    size: font.size ?? undefined,
    bold: font.bold ?? undefined,
    italic: font.italic ?? undefined,
    color: font.color ?? undefined,
  };
}

/** Apply a captured font snapshot, skipping absent values. */
function applyFontSnap(font: PowerPoint.ShapeFont, snap: FontSnap): void {
  if (snap.name) font.name = snap.name;
  if (snap.size) font.size = snap.size;
  if (snap.bold) font.bold = snap.bold;
  if (snap.italic) font.italic = snap.italic;
  if (snap.color) font.color = snap.color;
}

/** The active (selected) slide. */
async function getActiveSlide(context: PowerPoint.RequestContext): Promise<PowerPoint.Slide> {
  const sel = context.presentation.getSelectedSlides();
  sel.load("items");
  await context.sync();
  const slide = sel.items[0];
  if (!slide) throw new Error("Couldn't detect the active slide. Please try again.");
  return slide;
}

/** Clear the text of every selected shape (empties table cells too). */
export async function deleteAllText(): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const sel = context.presentation.getSelectedShapes();
    sel.load("items");
    await context.sync();
    if (sel.items.length === 0) throw new Error("Select at least one shape first.");
    for (const shape of sel.items) shape.load("type");
    await context.sync();

    for (const shape of sel.items) {
      try {
        if (String(shape.type) === "Table") {
          const table = shape.getTable();
          table.load("rowCount,columnCount");
          await context.sync();
          for (let r = 0; r < table.rowCount; r++) {
            for (let c = 0; c < table.columnCount; c++) table.getCellOrNullObject(r, c).text = "";
          }
        } else {
          shape.textFrame.textRange.text = "";
        }
        await context.sync();
      } catch {
        /* shapes without a text frame (images, etc.) are skipped */
      }
    }
  });
}

/** Split the first selected text box into one box per line/paragraph. */

const LINE_BREAKS = /[\r\n\v]+/g;

export async function splitTextByParagraphs(): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const sel = context.presentation.getSelectedShapes();
    sel.load("items");
    await context.sync();
    if (sel.items.length === 0) throw new Error("Select a text shape first.");
    const shape = sel.items[0]!;
    shape.load("left,top,width,height");
    shape.textFrame.textRange.load("text");
    shape.textFrame.textRange.font.load("name,size,bold,italic,color");
    await context.sync();

    const lines = (shape.textFrame.textRange.text || "")
      .split(LINE_BREAKS)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length <= 1) throw new Error("Add more than one line to split.");

    const slide = await getActiveSlide(context);
    const { left, top, width, height } = shape;
    const lineH = height / Math.max(lines.length, 1);
    const fontSnap = readFontSnap(shape.textFrame.textRange.font);

    for (let i = 0; i < lines.length; i++) {
      const tb = slide.shapes.addTextBox(lines[i]!, {
        left,
        top: top + i * (lineH + 4),
        width,
        height: lineH,
      });
      applyFontSnap(tb.textFrame.textRange.font, fontSnap);
    }
    shape.delete();
    await context.sync();
  });
}

/** Merge the selected text boxes into one, keeping the first box's font. */
export async function mergeTextBoxes(): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const { shapes } = await getSelectedShapesWithRef(context, 2);
    // Reading order: top-to-bottom, then left-to-right.
    const sorted = [...shapes].sort((a, b) => (Math.abs(a.top - b.top) > 10 ? a.top - b.top : a.left - b.left));

    for (const s of sorted) {
      try {
        s.shape.textFrame.textRange.load("text");
      } catch {
        /* no text frame */
      }
    }
    sorted[0]!.shape.textFrame.textRange.font.load("name,size,bold,italic,color");
    await context.sync();

    const texts: string[] = [];
    for (const s of sorted) {
      try {
        const t = s.shape.textFrame.textRange.text;
        if (t?.trim()) texts.push(t.trim());
      } catch {
        /* no text frame */
      }
    }
    if (texts.length === 0) throw new Error("No text found in the selected shapes.");

    const slide = await getActiveSlide(context);
    const minL = Math.min(...sorted.map((s) => s.left));
    const minT = Math.min(...sorted.map((s) => s.top));
    const maxR = Math.max(...sorted.map((s) => s.right));
    const maxB = Math.max(...sorted.map((s) => s.bottom));

    const tb = slide.shapes.addTextBox(texts.join("\n"), {
      left: minL,
      top: minT,
      width: maxR - minL,
      height: maxB - minT,
    });
    applyFontSnap(tb.textFrame.textRange.font, readFontSnap(sorted[0]!.shape.textFrame.textRange.font));

    for (const s of sorted) s.shape.delete();
    await context.sync();
  });
}

async function transformSelectedShapeText(
  transform: (text: string) => string | null,
): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const sel = context.presentation.getSelectedShapes();
    sel.load("items");
    await context.sync();
    if (sel.items.length === 0) throw new Error("Select a shape first.");

    const frames = sel.items.map((shape) => shape.getTextFrameOrNullObject());
    for (const frame of frames) frame.load("isNullObject");
    await context.sync();
    for (const frame of frames) {
      if (!frame.isNullObject) frame.textRange.load("text");
    }
    await context.sync();

    for (const frame of frames) {
      if (frame.isNullObject) continue;
      const next = transform(frame.textRange.text);
      if (next !== null) frame.textRange.text = next;
    }
    await context.sync();
  });
}

/** Sort each selected shape's lines alphabetically (asc/desc). */
async function sortShapeText(order: "asc" | "desc"): Promise<void> {
  await transformSelectedShapeText((text) => {
    if (!text?.trim()) return null;
    const lines = text.split(LINE_BREAKS).filter((l) => l.trim());
    lines.sort((a, b) =>
      order === "asc"
        ? a.localeCompare(b, undefined, { sensitivity: "base" })
        : b.localeCompare(a, undefined, { sensitivity: "base" }),
    );
    return lines.join("\n");
  });
}

export const sortTextAscending = (): Promise<void> => sortShapeText("asc");
export const sortTextDescending = (): Promise<void> => sortShapeText("desc");

/** Collapse line breaks in each selected shape to single spaces. */
export async function removeLineBreaks(): Promise<void> {
  await transformSelectedShapeText((text) => {
    if (!text) return null;
    const collapsed = text.replace(LINE_BREAKS, " ").replace(/\s{2,}/g, " ").trim();
    return collapsed !== text ? collapsed : null;
  });
}

/** Reorder the selected shapes into their position slots, sorted by text. */
/**
 * Reorder the selected shapes into their own position slots, following the
 * order they were **selected** (matching VSTO): the first-selected shape takes
 * the first slot, the second the next, and so on. The `axis` decides how the
 * target slots are ordered:
 *   horizontal → top-to-bottom, vertical → left-to-right,
 *   both → reading order (rows top-to-bottom, then left-to-right).
 */
export async function reorderShapesByText(axis: ReorderAxis = "both"): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    // `shapes` preserves the selection order returned by getSelectedShapes.
    const { shapes } = await getSelectedShapesWithRef(context, 2);

    const slotOrder = (a: { left: number; top: number }, b: { left: number; top: number }): number => {
      if (axis === "horizontal") return a.top - b.top || a.left - b.left;
      if (axis === "vertical") return a.left - b.left || a.top - b.top;
      return Math.abs(a.top - b.top) > 10 ? a.top - b.top : a.left - b.left;
    };
    const slots = shapes.map((s) => ({ left: s.left, top: s.top }));
    slots.sort(slotOrder);

    // Selection order → ordered slots: selected[i] moves to slot[i].
    for (let i = 0; i < shapes.length; i++) {
      shapes[i]!.shape.left = slots[i]!.left;
      shapes[i]!.shape.top = slots[i]!.top;
    }
    await context.sync();
  });
}

/** Replace the font in every cell of a table shape; returns the cells changed. */
async function replaceFontInTable(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
  fromLower: string,
  toFont: string,
): Promise<number> {
  const table = shape.getTable();
  table.load("rowCount,columnCount");
  await context.sync();
  let changed = 0;
  for (let r = 0; r < table.rowCount; r++) {
    for (let c = 0; c < table.columnCount; c++) {
      const cell = table.getCellOrNullObject(r, c);
      cell.font.load("name");
      await context.sync();
      if (cell.font.name?.toLowerCase() === fromLower) {
        cell.font.name = toFont;
        changed++;
      }
    }
  }
  return changed;
}

/** Replace the font on a non-table shape's text; returns 1 if changed, else 0. */
async function replaceFontInShape(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
  fromLower: string,
  toFont: string,
): Promise<number> {
  const font = shape.textFrame.textRange.font;
  font.load("name");
  await context.sync();
  if (font.name?.toLowerCase() === fromLower) {
    font.name = toFont;
    return 1;
  }
  return 0;
}

/**
 * Replace one font with another across the current slide (or all slides).
 * Returns the number of shapes/cells changed. Font is matched at the shape
 * level (Office.js has no per-paragraph API).
 */
export async function replaceFont(fromFont: string, toFont: string, allSlides = false): Promise<number> {
  if (!fromFont || !toFont) throw new Error("Both source and target fonts are required.");
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  const fromLower = fromFont.toLowerCase();
  let replaced = 0;

  await ppt().run(async (context) => {
    const slideColl = allSlides ? context.presentation.slides : context.presentation.getSelectedSlides();
    slideColl.load("items");
    await context.sync();

    for (const slide of slideColl.items) {
      const shapeColl = slide.shapes;
      shapeColl.load("items");
      await context.sync();
      for (const shape of shapeColl.items) shape.load("type");
      await context.sync();

      for (const shape of shapeColl.items) {
        try {
          replaced +=
            String(shape.type) === "Table"
              ? await replaceFontInTable(context, shape, fromLower, toFont)
              : await replaceFontInShape(context, shape, fromLower, toFont);
        } catch {
          /* shapes without text are skipped */
        }
      }
    }
    await context.sync();
  });
  return replaced;
}

/** Set the outline/connector weight (points) on the selected shapes. */
export async function setConnectorWeight(weight: number): Promise<void> {
  if (weight === undefined || weight === null) throw new Error("A line weight is required.");
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const sel = context.presentation.getSelectedShapes();
    sel.load("items");
    await context.sync();
    if (sel.items.length === 0) throw new Error("Select at least one shape first.");
    for (const shape of sel.items) shape.lineFormat.load("visible");
    await context.sync();
    for (const shape of sel.items) {
      try {
        shape.lineFormat.weight = weight;
      } catch {
        /* shapes without a line format are skipped */
      }
    }
    await context.sync();
  });
}

/* ── Table Tools ─────────────────────────────────────────────────── */

interface SelectedTable {
  shape: PowerPoint.Shape;
  table: PowerPoint.Table;
  rowCount: number;
  colCount: number;
  left: number;
  top: number;
  width: number;
  height: number;
}

/** The first table shape in the selection, with its geometry and grid size. */
async function getSelectedTable(context: PowerPoint.RequestContext): Promise<SelectedTable> {
  const sel = context.presentation.getSelectedShapes();
  sel.load("items");
  await context.sync();
  if (!sel.items || sel.items.length === 0) throw new Error("Select a table first.");
  for (const s of sel.items) s.load("type,left,top,width,height");
  await context.sync();

  const shape = sel.items.find((s) => String(s.type) === "Table");
  if (!shape) throw new Error("Select a table first.");
  const table = shape.getTable();
  table.load("rowCount,columnCount");
  await context.sync();

  return {
    shape,
    table,
    rowCount: table.rowCount,
    colCount: table.columnCount,
    left: shape.left,
    top: shape.top,
    width: shape.width,
    height: shape.height,
  };
}

interface CapturedCell {
  r: number;
  c: number;
  text: string;
  font: FontSnap;
  align: string | null;
  /** Explicit (live) cell fill — fallback when the OOXML style can't be resolved. */
  liveFill: string | null;
}

interface CapturedTable {
  left: number;
  top: number;
  width: number;
  height: number;
  rowCount: number;
  colCount: number;
  cells: CapturedCell[];
}

/** Read the selected table's geometry + each cell's text, font, alignment and explicit fill. */
async function captureSelectedTable(context: PowerPoint.RequestContext): Promise<CapturedTable> {
  const tbl = await getSelectedTable(context);
  const refs: Array<{ r: number; c: number; cell: PowerPoint.TableCell }> = [];
  for (let r = 0; r < tbl.rowCount; r++) {
    for (let c = 0; c < tbl.colCount; c++) {
      const cell = tbl.table.getCellOrNullObject(r, c);
      cell.load("text,horizontalAlignment");
      cell.font.load("name,size,bold,italic,color");
      cell.fill.load("type,foregroundColor");
      refs.push({ r, c, cell });
    }
  }
  await context.sync();

  return {
    left: tbl.left,
    top: tbl.top,
    width: tbl.width,
    height: tbl.height,
    rowCount: tbl.rowCount,
    colCount: tbl.colCount,
    cells: refs.map(({ r, c, cell }) => ({
      r,
      c,
      text: cell.text ?? "",
      font: readFontSnap(cell.font),
      align: cell.horizontalAlignment ?? null,
      liveFill: String(cell.fill.type ?? "").toLowerCase() === "solid" ? (cell.fill.foregroundColor ?? null) : null,
    })),
  };
}


async function tableToTextBoxes(gap: number): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");


  let captured: CapturedTable | null = null;
  await ppt().run(async (context) => {
    captured = await captureSelectedTable(context);
  });
  if (!captured) throw new Error("Couldn't read the table. Please try again.");
  const table: CapturedTable = captured;


  const fills = await resolveTableCellFills({
    leftPt: table.left,
    topPt: table.top,
    rowCount: table.rowCount,
    colCount: table.colCount,
  });


  await ppt().run(async (context) => {
    const slide = await getActiveSlide(context);


    const sel = context.presentation.getSelectedShapes();
    sel.load("items");
    await context.sync();
    for (const s of sel.items) s.load("type");
    await context.sync();
    const original = sel.items.find((s) => String(s.type) === "Table");

    const cellW = table.width / table.colCount;
    const cellH = table.height / table.rowCount;
    for (const cell of table.cells) {
      if (!cell.text.trim()) continue; 
      const tb = slide.shapes.addTextBox(cell.text, {
        left: table.left + cell.c * cellW + gap / 2,
        top: table.top + cell.r * cellH + gap / 2,
        width: cellW - gap,
        height: cellH - gap,
      });
      applyFontSnap(tb.textFrame.textRange.font, cell.font);
      if (cell.align) {
        tb.textFrame.textRange.paragraphFormat.horizontalAlignment =
          cell.align as PowerPoint.ParagraphHorizontalAlignment;
      }
      const fill = fills[cell.r]?.[cell.c] ?? cell.liveFill;
      if (fill) tb.fill.setSolidColor(`#${fill.replace("#", "")}`);
    }
    original?.delete();
    await context.sync(); // single mutation batch → single undo
  });
}


export async function tableToTextExplode(): Promise<void> {
  await tableToTextBoxes(6); // gap → detached "exploded" boxes
}


export async function tableToTextAsIs(): Promise<void> {
  await tableToTextBoxes(0); // flush → mirrors the table
}

/** The first table on a slide, loaded with its rows, or null. */
async function loadSlideTable(context: PowerPoint.RequestContext, slide: PowerPoint.Slide): Promise<PowerPoint.Table | null> {
  const shapes = slide.shapes;
  shapes.load("items");
  await context.sync();
  for (const s of shapes.items) s.load("type");
  await context.sync();
  const tableShape = shapes.items.find((s) => String(s.type) === "Table");
  if (!tableShape) return null;
  const table = tableShape.getTable();
  table.rows.load("items");
  await context.sync();
  return table;
}

/**
 * Duplicate the current slide (so the copy keeps the exact table style) and
 * split it across the copy. Office.js has no `Slide.duplicate()`, so the slide
 * is exported to base64 and re-inserted right after itself.
 *
 * Undo fidelity: we pre-trim the *moved* slide's table at the OOXML level (rows
 * [headers, boundary) removed from the exported deck), so the only *live*
 * mutations — deleting the moved rows from the original table and inserting the
 * pre-built slide — happen in a SINGLE `context.sync()`. That collapses the old
 * two-step (insert-then-trim) undo into one flush, so Ctrl+Z reverts the whole
 * split in one go on hosts that group a sync as one undo unit.
 *
 * The original keeps rows [0, boundary); the moved slide keeps the header rows
 * [0, headers) plus the moved rows [boundary, rowCount).
 */
async function duplicateSlideAndSplit(
  slideId: string,
  rowCount: number,
  headers: number,
  boundary: number,
): Promise<void> {
  // 1) Export the current slide → base64 (read-only).
  let base64 = "";
  await ppt().run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items");
    await context.sync();
    for (const s of slides.items) s.load("id");
    await context.sync();
    const slide = slides.items.find((s) => s.id === slideId);
    if (!slide) throw new Error("Couldn't find the active slide.");
    const result = slides.exportAsBase64Presentation([slide]);
    await context.sync();
    base64 = result.value;
  });

  // Pre-build the moved slide: keep headers [0, headers) + moved rows
  // [boundary, …) by dropping [headers, boundary) from the exported copy. This
  // is a pure OOXML edit, so no post-insert trim (and no extra sync) is needed.
  const movedSlide = removeTableRowsFromExportedSlide(base64, headers, boundary);

  // 2) One mutating sync: trim the original table AND insert the pre-built slide.
  await ppt().run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items");
    await context.sync();
    for (const s of slides.items) s.load("id");
    await context.sync();
    const orig = slides.items.find((s) => s.id === slideId);
    if (!orig) throw new Error("Couldn't find the active slide.");

    // Original keeps [0, boundary) → delete moved rows [boundary, rowCount) high→low.
    const table = await loadSlideTable(context, orig);
    if (table) {
      const end = Math.min(rowCount, table.rows.items.length);
      for (let r = end - 1; r >= boundary; r--) table.rows.items[r]?.delete();
    }
    context.presentation.insertSlidesFromBase64(movedSlide, {
      targetSlideId: slideId,
      formatting: "KeepSourceFormatting",
    });
    await context.sync(); // single flush → one undo entry (best effort per host)
  });
}

/**
 * Split the selected table across two slides (ported from the VSTO plugin).
 * The current slide is duplicated so both halves keep the real table style;
 * then rows are deleted from each: the original keeps the rows above the split,
 * the duplicate keeps the top `headerRows` repeated plus the rows from the
 * split down. `splitRow` and `headerRows` are 1-based, matching the VSTO panel.
 */
export async function splitTable(headerRows: number, splitRow: number): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");

  let info: { rowCount: number; slideId: string } | null = null;
  await ppt().run(async (context) => {
    const tbl = await getSelectedTable(context);
    const sel = context.presentation.getSelectedSlides();
    sel.load("items");
    await context.sync();
    const slide = sel.items[0];
    if (!slide) throw new Error("Couldn't detect the active slide. Please try again.");
    slide.load("id");
    await context.sync();
    info = { rowCount: tbl.rowCount, slideId: slide.id };
  });
  if (!info) throw new Error("Couldn't read the table. Please try again.");
  const { rowCount, slideId } = info;

  const headers = Math.max(1, Math.floor(headerRows));
  const split = Math.floor(splitRow);
  if (split < 1 || split >= rowCount - 1) {
    throw new Error(`Enter a split row between 1 and ${rowCount - 2}.`);
  }
  // 0-based index where the moved (second) part begins — matches VSTO's split+header.
  const boundary = split + headers - 1;

  try {
    await duplicateSlideAndSplit(slideId, rowCount, headers, boundary);
  } catch {
    // The thrown error is logged centrally by useFormatTools; surface a
    // user-friendly message here.
    throw new Error("Couldn't split this table. Save the file (Ctrl+S) and try again on a current version of PowerPoint.");
  }
}

/** A table cell's rectangle in points, derived from row heights + column widths. */
interface CellRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Position a `size`×`size` shape inside a cell rect at the chosen 3×3 anchor. */
function anchorInCell(
  anchor: TableCellAnchor,
  cell: CellRect,
  size: number,
): { left: number; top: number } {
  const leftBy = { L: cell.left, C: cell.left + cell.width / 2 - size / 2, R: cell.left + cell.width - size };
  const topBy = { T: cell.top, M: cell.top + cell.height / 2 - size / 2, B: cell.top + cell.height - size };
  const v = anchor[0] as "T" | "M" | "B";
  const h = anchor[1] as "L" | "C" | "R";
  return { left: leftBy[h], top: topBy[v] };
}

/** Per-cell edges of a table, derived from its top-left + row/column sizes. */
interface CellGeometry {
  rowTops: number[];
  rowHeights: number[];
  colLefts: number[];
  colWidths: number[];
}

/** Build the cell grid from the table's top-left and cumulative row/col sizes. */
function buildCellGeometry(
  tableLeft: number,
  tableTop: number,
  rowHeights: number[],
  colWidths: number[],
): CellGeometry {
  const rowTops: number[] = [];
  let accTop = tableTop;
  for (const height of rowHeights) {
    rowTops.push(accTop);
    accTop += height;
  }
  const colLefts: number[] = [];
  let accLeft = tableLeft;
  for (const width of colWidths) {
    colLefts.push(accLeft);
    accLeft += width;
  }
  return { rowTops, rowHeights, colLefts, colWidths };
}

/** The first cell (margin-shifted) whose rect contains the point, or null. */
function findCellAt(
  midX: number,
  midY: number,
  geom: CellGeometry,
  horizontalSpace: number,
  verticalSpace: number,
): CellRect | null {
  for (let row = 0; row < geom.rowHeights.length; row++) {
    for (let col = 0; col < geom.colWidths.length; col++) {
      const cell: CellRect = {
        left: geom.colLefts[col]! + horizontalSpace,
        top: geom.rowTops[row]! + verticalSpace,
        width: geom.colWidths[col]!,
        height: geom.rowHeights[row]!,
      };
      if (
        midX >= cell.left &&
        midX <= cell.left + cell.width &&
        midY >= cell.top &&
        midY <= cell.top + cell.height
      ) {
        return cell;
      }
    }
  }
  return null;
}

/**
 * Align the selected shapes onto the cells of the first table on the slide
 * (ported from the VSTO "Align shapes in table" dialog). Each shape is resized
 * to a `size`×`size` square; a shape whose centre falls inside a cell (with the
 * horizontal/vertical margin added to the cell's origin) is snapped to that
 * cell at the chosen 3×3 anchor. Shapes not over any cell are left in place
 * (but still resized), and the table itself is never moved or resized.
 *
 * Office.js exposes no per-cell geometry, so each cell's rectangle is computed
 * from the table's top-left plus the cumulative row heights and column widths.
 */
export async function alignShapesInTable(cfg: AlignInTableConfig): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  await ppt().run(async (context) => {
    const selected = context.presentation.getSelectedShapes();
    selected.load("items");
    await context.sync();
    if (!selected.items || selected.items.length === 0) {
      throw new Error("Select the shapes to align onto the table.");
    }
    for (const s of selected.items) s.load("type,left,top,width,height");
    await context.sync();

    // The shapes to align: everything selected except a table (so a table caught
    // in the selection is never resized or moved).
    const movers = selected.items
      .filter((s) => String(s.type) !== "Table")
      .map((s) => ({ shape: s, left: s.left, top: s.top }));
    if (movers.length === 0) throw new Error("Select the shapes to align (not the table).");

    // Find the first table on the active slide.
    const slide = await getActiveSlide(context);
    slide.shapes.load("items");
    await context.sync();
    for (const s of slide.shapes.items) s.load("type,left,top");
    await context.sync();
    const tableShape = slide.shapes.items.find((s) => String(s.type) === "Table");
    if (!tableShape) throw new Error("No table found on the current slide.");

    // Cell geometry = table top-left + cumulative row heights / column widths.
    const table = tableShape.getTable();
    table.rows.load("items");
    table.columns.load("items");
    await context.sync();
    for (const r of table.rows.items) r.load("height");
    for (const c of table.columns.items) c.load("width");
    await context.sync();

    const geom = buildCellGeometry(
      tableShape.left,
      tableShape.top,
      table.rows.items.map((r) => r.height),
      table.columns.items.map((c) => c.width),
    );

    const { size, horizontalSpace, verticalSpace, anchor } = cfg;

    for (const m of movers) {
      // Resize to a square first; PowerPoint keeps the top-left fixed, so the new
      // centre is the captured top-left plus half the new size (matches VSTO).
      m.shape.width = size;
      m.shape.height = size;
      // Snap into the cell whose margin-shifted rect contains the centre.
      const cell = findCellAt(m.left + size / 2, m.top + size / 2, geom, horizontalSpace, verticalSpace);
      if (cell) {
        const pos = anchorInCell(anchor, cell, size);
        m.shape.left = pos.left;
        m.shape.top = pos.top;
      }
    }
    await context.sync();
  });
}

/** A shape's geometry captured by id, so a live preview can be reverted. */
export interface ShapeGeomSnapshot {
  id: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Snapshot the geometry of the selected non-table shapes (for Cancel/revert). */
export async function captureSelectionGeometry(): Promise<ShapeGeomSnapshot[]> {
  if (!hasPpt()) return [];
  const out: ShapeGeomSnapshot[] = [];
  await ppt().run(async (context) => {
    const sel = context.presentation.getSelectedShapes();
    sel.load("items");
    await context.sync();
    for (const s of sel.items) s.load("id,type,left,top,width,height");
    await context.sync();
    for (const s of sel.items) {
      if (String(s.type) === "Table") continue;
      out.push({ id: s.id, left: s.left, top: s.top, width: s.width, height: s.height });
    }
  });
  return out;
}

/** Restore shapes to a captured geometry snapshot (matched by id). */
export async function restoreShapeGeometry(snapshot: ShapeGeomSnapshot[]): Promise<void> {
  if (!hasPpt() || snapshot.length === 0) return;
  const byId = new Map(snapshot.map((s) => [s.id, s]));
  await ppt().run(async (context) => {
    const sel = context.presentation.getSelectedShapes();
    sel.load("items");
    await context.sync();
    for (const s of sel.items) s.load("id");
    await context.sync();
    for (const s of sel.items) {
      const snap = byId.get(s.id);
      if (!snap) continue;
      s.left = snap.left;
      s.top = snap.top;
      s.width = snap.width;
      s.height = snap.height;
    }
    await context.sync();
  });
}

